import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Pause, Play, SkipForward } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/account/standing-orders`;
const opts = { withCredentials: true };
const fmt = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "—");
const ghost = "inline-flex items-center gap-1.5 px-4 py-2 border border-[#303B2D] text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/75 rounded-sm hover:border-[#B5A642]/60 hover:text-[#E8DDC7] transition-colors disabled:opacity-40";

const StandingCard = ({ s, t, lang, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState("");
  const act = async (path, body) => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/${s.subscription_id}/${path}`, body, opts);
      onChange(r.data); setPicking(false);
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("That didn't go through. Please try again.", "Cela n'a pas fonctionné. Veuillez réessayer."));
    } finally { setBusy(false); }
  };
  const id = s.subscription_id;
  return (
    <li className="border border-[#303B2D]/70 rounded-sm p-6" data-testid={`standing-${id}`}>
      <ul className="space-y-1">
        {(s.items || []).map((i) => <li key={i.name} className="font-display text-xl text-[#E8DDC7]">{i.qty} × {i.name}</li>)}
      </ul>
      <p className="mt-3 inline-flex items-center gap-2 text-[14px] text-[#D3CEC4]/75" data-testid={`standing-next-${id}`}>
        <CalendarClock className="w-4 h-4 text-[#B5A642]" />
        {s.paused
          ? (s.pause_kind === "skip"
            ? t(`Skipping one delivery. Next one ${fmt(s.next_delivery_at, lang)}.`, `Une livraison sautée. Prochaine le ${fmt(s.next_delivery_at, lang)}.`)
            : t(`Paused until ${fmt(s.paused_until, lang)}. Next delivery ${fmt(s.next_delivery_at, lang)}.`, `En pause jusqu'au ${fmt(s.paused_until, lang)}. Prochaine livraison le ${fmt(s.next_delivery_at, lang)}.`))
          : t(`Next delivery ${fmt(s.next_delivery_at, lang)}.`, `Prochaine livraison le ${fmt(s.next_delivery_at, lang)}.`)}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {s.paused ? (
          <button className={ghost} disabled={busy} onClick={() => act("resume")} data-testid={`standing-resume-${id}`}>
            <Play className="w-3.5 h-3.5" /> {t("Resume now", "Reprendre maintenant")}
          </button>
        ) : (
          <>
            <button className={ghost} disabled={busy} onClick={() => act("skip")} data-testid={`standing-skip-${id}`}>
              <SkipForward className="w-3.5 h-3.5" /> {t("Skip the next one", "Sauter la prochaine")}
            </button>
            <button className={ghost} disabled={busy} onClick={() => setPicking((v) => !v)} data-testid={`standing-pause-${id}`}>
              <Pause className="w-3.5 h-3.5" /> {t("Pause", "Mettre en pause")}
            </button>
          </>
        )}
      </div>
      {picking && !s.paused && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`standing-pause-options-${id}`}>
          <span className="text-[13px] text-[#D3CEC4]/60">{t("Pause for", "Pause de")}</span>
          {[1, 2, 3].map((m) => (
            <button key={m} disabled={busy} onClick={() => act("pause", { months: m })} data-testid={`standing-pause-${m}-${id}`}
              className="px-3 py-1.5 border border-[#B5A642]/50 text-[12px] text-[#E8DDC7] rounded-sm hover:bg-[#B5A642]/10 transition-colors disabled:opacity-40">
              {m} {m === 1 ? t("month", "mois") : t("months", "mois")}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-3 text-[13px] text-[#C9876B]" data-testid={`standing-error-${id}`}>{error}</p>}
      <p className="mt-4 text-[12px] text-[#D3CEC4]/50">{t("Nothing is charged or shipped while paused. Deliveries restart on their own.", "Rien n'est facturé ni expédié pendant la pause. Les livraisons reprennent d'elles-mêmes.")}</p>
    </li>
  );
};

export const AccountStandingOrders = ({ t, lang }) => {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    axios.get(API, opts).then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => setSubs([]));
  }, []);
  if (!subs.length) return null;
  const update = (next) => setSubs((list) => list.map((s) => (s.subscription_id === next.subscription_id ? next : s)));
  return (
    <section className="mt-10" data-testid="account-standing-orders">
      <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80">{t("Standing Orders", "Commandes permanentes")}</h2>
      <ul className="mt-6 space-y-5">{subs.map((s) => <StandingCard key={s.subscription_id} s={s} t={t} lang={lang} onChange={update} />)}</ul>
      <div className="hairline mt-12" />
    </section>
  );
};
